import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FieldArrayWithId, useFieldArray, useForm } from 'react-hook-form';
import clsx from 'clsx';

import { incomesApi } from 'prequalificationV2/api/IncomesApi';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { IncomeW2Type } from 'api/UnderwritingApi';
import { Button } from 'components/Buttons/Button';
import ButtonOption from 'components/Buttons/ButtonOption';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import Input from 'components/Inputs/Input';
import Section from 'prequalificationV2/components/LoanPortal/Sections/Section';
import { BorrowerIncomeType } from 'enums/BorrowerIncomeType';
import { BorrowerRole } from 'enums/BorrowerRole';
import { ButtonType } from 'enums/ButtonType';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { convertInputSelectValue } from 'prequalificationV2/utils/inputSelect';
import { maskMonetaryValue, removeNonDigits } from 'prequalificationV2/utils/stringUtils';
import { updateApplicationInStore } from 'prequalificationV2/utils/updateApplicationInStore';
import { updateDocuments } from 'prequalificationV2/utils/updateDocuments';
import { updateRequiredInformation } from 'prequalificationV2/utils/updateRequiredInformation';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';

import {
  ANNUAL_INCOME_LABEL,
  INCOME_BUTTON_TYPES,
  INCOME_LABEL_BY_TYPE,
  MAXIMUM_INCOME_AMOUNT_LENGTH,
  MAXIMUM_OTHER_DESCRIPTION_LENGTH,
  OTHER_INCOME_SOURCE_CAPTION,
  OTHER_INCOME_SOURCE_LABEL,
  W2_CHECKBOX_LABEL,
  W2_EMPLOYMENT_TYPE_LABEL,
} from './borrowerIncomeInfo.constants';
import {
  buildIncomeFormValues,
  createIncomeFormEntry,
  getAnnualIncomeCaption,
  getHasReachedMaximumIncomeSources,
  getTotalAnnualIncome,
  isW2IncomeType,
  serializeIncomeFormValues,
  sortIncomeFormEntries,
} from './borrowerIncomeInfo.utils';
import styles from './BorrowerIncomeInfo.module.scss';
import EmployerCompanySelect from './components/EmployerCompanySelect';
import { CombinedIncomeType, IncomeFormValues } from './borrowerIncomeInfo.types';

interface BorrowerIncomeInfoProps {
  borrowerType: BorrowerRole;
  isIncomeInformationComplete: boolean;
}

const BorrowerIncomeInfo = ({ borrowerType, isIncomeInformationComplete }: BorrowerIncomeInfoProps) => {
  const dispatch = useAppDispatch();
  const applicationId = useAppSelector((state) => state.application.applicationId);
  const applicationVariables = useAppSelector((state) => state.application.variables);
  const storeIncomeFormValues = useMemo(() => {
    return buildIncomeFormValues(applicationVariables, borrowerType);
  }, [applicationVariables, borrowerType]);

  const { control, register, handleSubmit, watch, reset, setValue, clearErrors, formState, getValues } =
    useForm<IncomeFormValues>({ defaultValues: storeIncomeFormValues });
  const { fields, remove, insert } = useFieldArray({ control: control, name: 'incomes', keyName: 'fieldId' });

  const [isSaving, setIsSaving] = useState(false);
  const [saveVersion, setSaveVersion] = useState(0);
  const [selectedIncomeTypesError, setSelectedIncomeTypesError] = useState<string | undefined>();
  const hasRestoredEmployerIcons = useRef(false);

  const watcher = watch();

  useEffect(() => {
    reset(storeIncomeFormValues);
    setSelectedIncomeTypesError(undefined);
    hasRestoredEmployerIcons.current = false;
  }, [storeIncomeFormValues, reset]);

  useEffect(() => {
    if (hasRestoredEmployerIcons.current) {
      return;
    }

    hasRestoredEmployerIcons.current = true;

    const restoreEmployerCompanyIcons = async () => {
      const restorePromises = storeIncomeFormValues.incomes
        .filter((incomeFormEntry) => {
          return (
            isW2IncomeType(incomeFormEntry.incomeType) &&
            incomeFormEntry.employerCompany?.label &&
            !incomeFormEntry.employerCompany.iconUrl &&
            incomeFormEntry.employerCompany.payrollProviderMappingId
          );
        })
        .map(async (incomeFormEntry) => {
          const employerCompaniesResponse = await preQualificationApi.getCompanies(
            incomeFormEntry.employerCompany!.label,
          );
          const matchingEmployerCompany = employerCompaniesResponse.companies.find((company) => {
            return company.companyMappingId === incomeFormEntry.employerCompany!.payrollProviderMappingId;
          });

          return { incomeType: incomeFormEntry.incomeType, matchingEmployerCompany: matchingEmployerCompany };
        });

      const settledResults = await Promise.allSettled(restorePromises);

      settledResults.forEach((settledResult) => {
        if (settledResult.status === 'fulfilled' && settledResult.value.matchingEmployerCompany) {
          const { incomeType, matchingEmployerCompany } = settledResult.value;
          const currentIndex = getValues('incomes').findIndex((incomeFormEntry) => {
            return incomeFormEntry.incomeType === incomeType;
          });

          if (currentIndex === -1) {
            return;
          }

          setValue(`incomes.${currentIndex}.employerCompany`, {
            label: matchingEmployerCompany.companyName,
            value: convertInputSelectValue(matchingEmployerCompany.companyName),
            iconUrl: matchingEmployerCompany.logoUrl,
            payrollProviderMappingId: matchingEmployerCompany.companyMappingId,
          });
        }
      });
    };

    void restoreEmployerCompanyIcons();
  }, [storeIncomeFormValues, getValues, setValue]);

  useEffect(() => {
    fields.forEach((field, index) => {
      if (field.incomeType === BorrowerIncomeType.HomemakerUnemployed) {
        return;
      }

      register(`incomes.${index}.annualIncome`, {
        validate: (value) => Boolean(Number(removeNonDigits(value))) || `${ANNUAL_INCOME_LABEL} is required`,
      });

      if (isW2IncomeType(field.incomeType)) {
        register(`incomes.${index}.w2EmploymentType`, {
          validate: (value) => Boolean(value) || `${W2_EMPLOYMENT_TYPE_LABEL} is required`,
        });
      }

      if (field.incomeType === BorrowerIncomeType.Other) {
        register(`incomes.${index}.otherDescription`, {
          validate: (value) => Boolean(value?.trim().length) || OTHER_INCOME_SOURCE_LABEL,
        });
      }
    });
  }, [fields, register]);

  const hasSavedIncomeValues = fields.length > 0;
  const isSecondW2IncomeSelected = fields.some((field) => field.incomeType === BorrowerIncomeType.W2SecondEmployer);
  const isThirdW2IncomeSelected = fields.some((field) => field.incomeType === BorrowerIncomeType.W2ThirdEmployer);
  const totalAnnualIncome = getTotalAnnualIncome(watcher);

  const addIncomeType = (incomeType: CombinedIncomeType) => {
    if (getHasReachedMaximumIncomeSources(fields.map((field) => field.incomeType))) {
      setSelectedIncomeTypesError('You have reached the maximum number of income types');

      return;
    }

    const newIncomeFormEntry = createIncomeFormEntry(incomeType);
    const insertionIndex = sortIncomeFormEntries([...fields, newIncomeFormEntry]).findIndex((incomeFormEntry) => {
      return incomeFormEntry.incomeType === incomeType;
    });

    insert(insertionIndex, newIncomeFormEntry);
    setSelectedIncomeTypesError(undefined);
  };

  const handleIncomeTypeButtonClick = (combinedIncomeType: CombinedIncomeType) => {
    const index = fields.findIndex((field) => field.incomeType === combinedIncomeType);

    if (index !== -1) {
      if (combinedIncomeType === BorrowerIncomeType.W2) {
        const indexesToRemove = [
          index,
          fields.findIndex((field) => field.incomeType === BorrowerIncomeType.W2SecondEmployer),
          fields.findIndex((field) => field.incomeType === BorrowerIncomeType.W2ThirdEmployer),
        ].filter((foundIndex) => foundIndex !== -1);

        remove(indexesToRemove);
      } else {
        remove(index);
      }

      setSelectedIncomeTypesError(undefined);

      return;
    }

    addIncomeType(combinedIncomeType);
  };

  const handleAdditionalW2IncomeCheckboxChange = (
    additionalW2IncomeType: BorrowerIncomeType.W2SecondEmployer | BorrowerIncomeType.W2ThirdEmployer,
  ) => {
    const index = fields.findIndex((field) => field.incomeType === additionalW2IncomeType);

    if (index !== -1) {
      const indexesToRemove = [index];

      if (additionalW2IncomeType === BorrowerIncomeType.W2SecondEmployer) {
        const thirdIndex = fields.findIndex((field) => field.incomeType === BorrowerIncomeType.W2ThirdEmployer);

        if (thirdIndex !== -1) {
          indexesToRemove.push(thirdIndex);
        }
      }

      remove(indexesToRemove);
      setSelectedIncomeTypesError(undefined);

      return;
    }

    addIncomeType(additionalW2IncomeType);
  };

  const onSubmit = handleSubmit(async (incomeFormValues) => {
    if (!applicationId) {
      return;
    }

    if (!incomeFormValues.incomes.length) {
      setSelectedIncomeTypesError('Please select at least one income source');

      return;
    }

    setIsSaving(true);

    try {
      const serializedIncomeFormValues = serializeIncomeFormValues(incomeFormValues);
      const updateIncomeInfoResponse = await incomesApi.updateIncomeInfo({
        applicationId: applicationId,
        borrowerType: borrowerType,
        incomes: serializedIncomeFormValues.incomes,
        isHomemakerUnemployed: serializedIncomeFormValues.isHomemakerUnemployed,
        other: serializedIncomeFormValues.other,
        w2Incomes: serializedIncomeFormValues.w2Incomes,
      });

      updateApplicationInStore(updateIncomeInfoResponse, dispatch);
      updateRequiredInformation(updateIncomeInfoResponse, dispatch);
      updateDocuments(updateIncomeInfoResponse, dispatch);
      setSaveVersion((currentSaveVersion) => {
        return currentSaveVersion + 1;
      });
    } catch {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsSaving(false);
    }
  });

  const renderIncomeFields = (field: FieldArrayWithId<IncomeFormValues, 'incomes', 'fieldId'>, index: number) => {
    if (field.incomeType === BorrowerIncomeType.HomemakerUnemployed) {
      return null;
    }

    if (isW2IncomeType(field.incomeType)) {
      return (
        <div key={field.fieldId} className={styles.w2IncomeContainer}>
          <div className={styles.incomeTypeContainer}>
            <span className={styles.incomeTypeLabel}>{W2_EMPLOYMENT_TYPE_LABEL}</span>
            <div className={styles.incomeTypeOptions}>
              {Object.values(IncomeW2Type).map((option) => (
                <Checkbox
                  key={option}
                  name={`${field.incomeType}-employmentType`}
                  type={CheckboxType.Primary}
                  isChecked={watcher.incomes[index]?.w2EmploymentType === option}
                  text={option}
                  disabled={isIncomeInformationComplete}
                  onChange={() => {
                    setValue(`incomes.${index}.w2EmploymentType`, option, { shouldDirty: true });
                    clearErrors(`incomes.${index}.w2EmploymentType`);
                  }}
                />
              ))}
            </div>
            {formState.errors.incomes?.[index]?.w2EmploymentType?.message && (
              <p className={styles.incomeTypeError}>{formState.errors.incomes[index]?.w2EmploymentType?.message}</p>
            )}
          </div>
          <div className={styles.incomeContainer}>
            <EmployerCompanySelect control={control} index={index} disabled={isIncomeInformationComplete} />
            <Input
              label={INCOME_LABEL_BY_TYPE[field.incomeType]}
              className={styles.input}
              value={maskMonetaryValue(watcher.incomes[index]?.annualIncome)}
              maxLength={MAXIMUM_INCOME_AMOUNT_LENGTH}
              caption={getAnnualIncomeCaption(field.incomeType)}
              inputMode="numeric"
              disabled={isIncomeInformationComplete}
              errorMessage={formState.errors.incomes?.[index]?.annualIncome?.message}
              onChange={(event) => {
                setValue(`incomes.${index}.annualIncome`, removeNonDigits(event.target.value), {
                  shouldDirty: true,
                });
                clearErrors(`incomes.${index}.annualIncome`);
              }}
            />
            {field.incomeType === BorrowerIncomeType.W2 && (
              <Checkbox
                name={'secondW2Income'}
                type={CheckboxType.Alternative}
                isChecked={isSecondW2IncomeSelected}
                onChange={() => {
                  handleAdditionalW2IncomeCheckboxChange(BorrowerIncomeType.W2SecondEmployer);
                }}
                text={W2_CHECKBOX_LABEL}
                className={styles.checkboxText}
                disabled={isIncomeInformationComplete || isThirdW2IncomeSelected}
              />
            )}
            {field.incomeType === BorrowerIncomeType.W2SecondEmployer && (
              <Checkbox
                name={'thirdW2Income'}
                type={CheckboxType.Alternative}
                isChecked={isThirdW2IncomeSelected}
                onChange={() => {
                  handleAdditionalW2IncomeCheckboxChange(BorrowerIncomeType.W2ThirdEmployer);
                }}
                text={W2_CHECKBOX_LABEL}
                className={styles.checkboxText}
                disabled={isIncomeInformationComplete}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={field.fieldId} className={styles.incomeContainer}>
        <Input
          label={INCOME_LABEL_BY_TYPE[field.incomeType]}
          className={styles.input}
          value={maskMonetaryValue(watcher.incomes[index]?.annualIncome)}
          maxLength={MAXIMUM_INCOME_AMOUNT_LENGTH}
          caption={getAnnualIncomeCaption(field.incomeType)}
          inputMode="numeric"
          disabled={isIncomeInformationComplete}
          errorMessage={formState.errors.incomes?.[index]?.annualIncome?.message}
          onChange={(event) => {
            setValue(`incomes.${index}.annualIncome`, removeNonDigits(event.target.value), { shouldDirty: true });
            clearErrors(`incomes.${index}.annualIncome`);
          }}
        />
        {field.incomeType === BorrowerIncomeType.Other && (
          <Input
            label={OTHER_INCOME_SOURCE_LABEL}
            className={styles.other}
            value={watcher.incomes[index]?.otherDescription}
            caption={OTHER_INCOME_SOURCE_CAPTION}
            errorMessage={formState.errors.incomes?.[index]?.otherDescription?.message}
            maxLength={MAXIMUM_OTHER_DESCRIPTION_LENGTH}
            disabled={isIncomeInformationComplete}
            onChange={(event) => {
              setValue(`incomes.${index}.otherDescription`, event.target.value, { shouldDirty: true });
              clearErrors(`incomes.${index}.otherDescription`);
            }}
          />
        )}
      </div>
    );
  };

  const totalIncomeLabel =
    borrowerType === BorrowerRole.Borrower ? 'Total Annual Borrower Income' : 'Total Annual Co-Borrower Income';

  return (
    <Section
      key={`${isIncomeInformationComplete}-${saveVersion}`}
      title={'Income Types'}
      subSectionStates={[
        {
          isExist: true,
          isCompleted: isIncomeInformationComplete || hasSavedIncomeValues,
        },
      ]}
    >
      <div className={clsx(styles.sectionBody, { [styles.sectionBodyDisabled]: isIncomeInformationComplete })}>
        <div className={styles.buttonContainer}>
          {INCOME_BUTTON_TYPES.map((incomeType) => {
            return (
              <ButtonOption
                key={incomeType}
                className={styles.button}
                active={fields.some((field) => field.incomeType === incomeType)}
                disabled={isIncomeInformationComplete}
                onClick={() => {
                  handleIncomeTypeButtonClick(incomeType);
                }}
              >
                {INCOME_LABEL_BY_TYPE[incomeType]}
              </ButtonOption>
            );
          })}
        </div>
        {selectedIncomeTypesError && <div className={styles.errorMessage}>{selectedIncomeTypesError}</div>}
        <div className={styles.incomeFieldsContainer}>{fields.map(renderIncomeFields)}</div>
        <div className={styles.sumContainer}>
          <p className={styles.sumTitle}>{totalIncomeLabel}</p>
          <p className={styles.sumValue}>{maskMonetaryValue(totalAnnualIncome) || '$0'}</p>
        </div>
        {!isIncomeInformationComplete && (
          <div className={styles.buttonControlContainer}>
            <Button
              className={styles.saveButton}
              buttonType={ButtonType.Primary}
              disabled={!formState.isDirty}
              isLoading={isSaving}
              onClick={() => {
                void onSubmit();
              }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </Section>
  );
};

export default BorrowerIncomeInfo;
